import React, { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { array, bool, object, oneOfType } from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import ScriptLoader from 'yoda-core-components/lib/helpers/ScriptLoader/ScriptLoader';
import {
    styledForYouItemClick,
    styledForYouLoadSuccess,
    styledForYouModuleClick,
} from '../../actions/AnalyticsAction';
import { miniPDPOnStyledForYouOpen } from '../../actions/MiniPDPAction';

const StyledForYou = ({ actions, placementFromFE }) => {
    const {
        context: {
            messagesTexts: { styledForYouWidget: { promoCodeText = '' } = {} } = {},
            preferences: { styledForYouWidget: { scriptURL = '' } = {} } = {},
        } = {},
    } = useSelector((store) => store);
    const [isScriptLoaded, setIsScriptLoaded] = useState(false);
    const STYLED_FOR_YOU_WIDGET_CONTAINER_ID = 'styled-for-you-container';

    /* istanbul ignore next */
    const itemPricingOverride = (item) => {
        if (item.promotions.includes('with_code')) {
            const text = document.createElement('span');
            text.classList.add('promotional-text');
            text.innerHTML = promoCodeText;
            const priceContainer = document.querySelectorAll(`a[href="${item.affiliate_link}"]`);
            const price = priceContainer[0];
            const promotionalPrice = price.querySelectorAll('.stylitics-ui-sale-price')[0];
            promotionalPrice.append(text);
            promotionalPrice.classList.add('promotional-sale-price');
        }
    };

    /* istanbul ignore next */
    const onStyledForYouItemClick = (item, outfit) => {
        const skuData = item?.remote_id || '';
        const itemId = item?.item_id || '';
        const skuID = item?.other_client_item_ids?.sku_nbrs[0] || '';
        /* istanbul ignore next */
        if (!isEmpty(skuData)) {
            const options = {
                ppId: skuData.split('_')[0],
                skuId: skuID,
            };
            const customEvent = document.createEvent('CustomEvent');
            customEvent.initCustomEvent('miniPDP.open', true, true, options);
            document.dispatchEvent(customEvent);
            actions.miniPDPOnStyledForYouOpen(itemId);
            actions.styledForYouItemClick &&
                actions.styledForYouItemClick({
                    styledForYouID: outfit?.id?.toString() || '',
                    styledForYouPPID: options.ppId,
                    moduleName: 'styled for you',
                });
        }
    };

    /* istanbul ignore next */
    const widgetOptions = {
        src: scriptURL,
        async: true,
        defer: false,
        onSuccess: async () => {
            const widgetInstance = await StyliticsComposer.initialize({
                client: 'jcpenney',
                target: STYLED_FOR_YOU_WIDGET_CONTAINER_ID,
                config: {
                    modules: [
                        {
                            type: 'styled-for-you',
                            params: {
                                display: {
                                    defaultTab: 'browsed',
                                },
                            },
                            on: {
                                'mount.outfits': ({ outfits }) => {
                                    window &&
                                        outfits?.length > 0 &&
                                        actions.styledForYouLoadSuccess(window.digitalData);
                                },
                                'click.outfit': ({ outfit }) => {
                                    actions.styledForYouModuleClick({
                                        styledForYouID: outfit?.id?.toString() || '',
                                    });
                                },
                                'mount.item': ({ item }) => itemPricingOverride(item),
                            },
                            override: {
                                'click.item': ({ item, outfit }) => {
                                    onStyledForYouItemClick(item, outfit);
                                },
                            },
                        },
                    ],
                },
            });
            setIsScriptLoaded(true);
            await widgetInstance.start();
        },
    };

    const stylisticsMarkup = () => {
        const stylistics = `<style>
        .stylitics-styled-for-you-base {
            padding-bottom: 0px !important;
        }
        .stylitics-ui-carousel-outer {
            font-family: Open Sans,OpenSans,Helvetica,arial,sans-serif !important; 
        }
        .stylitics-ui-tab {
            color: #202020 !important;
            font-family: Open Sans,OpenSans,Helvetica,arial,sans-serif !important; 
        }
        .stylitics-anchor-item-name {
            color: #202020 !important;
            font-weight: 600 !important;
        }
        .stylitics-anchor-item-price {
            display: none !important;
        }
        .stylitics-ui-arrow-button {
            border-radius: 8px !important;
        }
        .stylitics-outfit-card-cta {
            color: #202020 !important;
            font-weight: 700 !important;
        }
        .stylitics-styled-for-you-modal-heading {
            color: #202020 !important;
            font-weight: 600 !important;
            margin: 14px 0 !important;
        }
        .stylitics-item-box-name {
            color: #202020 !important;
        }
        .stylitics-item-regular-price {
            color: #202020 !important;
            font-family: Open Sans,OpenSans,Helvetica,arial,sans-serif !important; 
            font-weight: 700 !important;
        }
        .stylitics-item-regular-price-slashed {
            color: #666666 !important;
            font-family: Open Sans,OpenSans,Helvetica,arial,sans-serif !important; 
        }
        .stylitics-ui-price {
            color: #202020 !important;
            font-family: Open Sans,OpenSans,Helvetica,arial,sans-serif !important; 
            font-weight: 700 !important;
        }
        .stylitics-item-price {
            color: #202020 !important;
            font-family: Open Sans,OpenSans,Helvetica,arial,sans-serif !important; 
            font-weight: 700 !important;
        }
        .stylitics-item-sale-price {
            color: #202020 !important;
            font-family: Open Sans,OpenSans,Helvetica,arial,sans-serif !important; 
            font-weight: 700 !important;
        }
        .promotional-sale-price {
            color: #E71324 !important;
            font-family: Open Sans,OpenSans,Helvetica,arial,sans-serif !important; 
            font-weight: 700 !important;
        }
        .promotional-text {
            font-style: italic !important;
            font-weight: 400 !important;
        }
        .stylitics-item-box-cta {
            border: 1px solid #E4E4E4 !important;
            border-radius: 3.46px;
            color: #202020 !important;
        }
        </style>`;
        return stylistics;
    };

    useEffect(() => {
        if (!isScriptLoaded) {
            ScriptLoader.load(widgetOptions);
        }

        /* istanbul ignore next */
        return () => {
            const { attributes = {} } = window?.digitalData?.page;
            delete attributes?.styledForYouModule;
        };
    }, []);

    return (
        <div
            className={`${placementFromFE ? dt(['mb-10']) : ''}`}
            dangerouslySetInnerHTML={{ __html: stylisticsMarkup() }}
            id={STYLED_FOR_YOU_WIDGET_CONTAINER_ID}
        />
    );
};

StyledForYou.defaultProps = {
    actions: {},
    placementFromFE: false,
};

StyledForYou.propTypes = {
    actions: oneOfType([array, object]),
    placementFromFE: bool,
};

const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators(
        {
            styledForYouItemClick,
            styledForYouLoadSuccess,
            styledForYouModuleClick,
            miniPDPOnStyledForYouOpen,
        },
        dispatch
    ),
});

export default connect(null, mapDispatchToProps)(StyledForYou);
